/**
 * One event, everything the coach needs for it, in the order the day happens:
 * answer, confirm, brief, crate out, run it, crate in, report.
 *
 * The screen is a state machine on top of one event. What it offers depends on
 * where the coach and the clock are:
 *
 *   not asked, qualified   -> they may volunteer an answer
 *   asked, not assigned    -> they answer, and may change it
 *   assigned               -> the answer controls go away, confirmation appears
 *   started                -> nothing to answer, the brief is the point
 *   finished               -> materials come back and the report is due
 *
 * canChangeAvailability() in lib/api.ts owns the first three transitions, so the
 * rule lives in one place and this screen reads it.
 */

import { useCallback, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useAuth } from '../../auth/AuthContext'
import {
  canChangeAvailability,
  canCoachSeeEvent,
  confirmAssignment,
  getEventDetail,
  getPostEventReport,
  respondToAvailability,
  withdrawFromAssignment,
  type EventDetail,
} from '../../lib/api'
import { useAsync } from '../../lib/useAsync'
import {
  AssignmentBadge,
  AvailabilityBadge,
  Avatar,
  Badge,
  Card,
  EmptyState,
  ErrorState,
  EventStatusBadge,
  Loading,
  Notice,
  PageHeader,
  Section,
  ServiceTag,
} from '../../components/ui'
import { ChevronLeftIcon } from '../../components/Icons'
import {
  formatDateTime,
  formatDayLong,
  formatDuration,
  formatTime,
  humanize,
  initials,
  pluralize,
  relativeTime,
} from '../../lib/format'
import { countdownLabel, dayLabel, eventPhase, useAction, useLiveReload, useNow } from './coachHooks'
import { ActionFeedback } from './coachUi'
import { EventMaterialsCard } from './EventMaterialsCard'
import { EventReportForm } from './EventReportForm'
import type { AvailabilityResponse, PostEventReport } from '../../types/db'

const ANSWERS: Array<{ value: AvailabilityResponse; label: string; saved: string }> = [
  {
    value: 'available',
    label: 'I am free',
    saved: 'Saved. Michel can see you are free for this one.',
  },
  {
    value: 'tentative',
    label: 'Maybe',
    saved: 'Saved as a maybe. Add a note below if it depends on something.',
  },
  {
    value: 'unavailable',
    label: 'Cannot make it',
    saved: 'Saved. Michel knows you cannot make this one.',
  },
]

export function CoachEventDetail() {
  const { eventId = '' } = useParams()
  const { profile } = useAuth()
  const coachId = profile?.id ?? ''
  const now = useNow()

  const { data, error, loading, reload } = useAsync(async () => {
    // The gate first: a coach who is not on it, was not asked, and is not
    // qualified for the service gets the not-open screen, never the brief.
    const visible = coachId ? await canCoachSeeEvent(eventId, coachId) : false
    if (!visible) return { detail: null, report: null }
    const [detail, report] = await Promise.all([
      getEventDetail(eventId),
      getPostEventReport(eventId, coachId),
    ])
    return { detail, report }
  }, [eventId, coachId])
  useLiveReload(reload)

  const action = useAction(reload)
  const [editingReport, setEditingReport] = useState(false)
  const [standingDown, setStandingDown] = useState(false)
  const [reason, setReason] = useState('')
  const [noteOpen, setNoteOpen] = useState(false)
  const [note, setNote] = useState('')

  const afterReport = useCallback(() => {
    setEditingReport(false)
    reload()
  }, [reload])

  // Keep whatever is on screen while a reload runs, so tapping an answer does
  // not blank the page the coach is reading.
  if (loading && !data) return <Loading label="Loading the brief" />
  if (error && !data) return <ErrorState message={error} onRetry={reload} />
  if (!data) return null

  if (!data.detail) {
    return (
      <EmptyState
        title="This event is not open to you"
        body="It may have been cancelled, or you may not be on it any more. Message Michel if that looks wrong."
        action={
          <Link className="btn btn--secondary btn--sm" to="/events">
            Back to my events
          </Link>
        }
      />
    )
  }

  const detail: EventDetail = data.detail
  const { event, staffing } = detail
  const myAssignment =
    detail.assignments.find((a) => a.coach_id === coachId && a.status !== 'cancelled') ?? null
  const myAvailability = detail.availabilities.find((a) => a.coach_id === coachId) ?? null
  const myStandDown = myAssignment
    ? null
    : (detail.assignments.find((a) => a.coach_id === coachId && a.status === 'cancelled') ?? null)
  const teammates = detail.assignments.filter(
    (a) => a.coach_id !== coachId && a.status !== 'cancelled',
  )

  const phase = eventPhase(event.starts_at, event.ends_at, now)
  const isCancelled = event.status === 'cancelled' || event.cancelled_at !== null
  const canAnswer = canChangeAvailability({ event, assignment: myAssignment })
  const answered = myAvailability !== null && myAvailability.response !== 'pending'
  const hoursToStart = (new Date(event.starts_at).getTime() - now.getTime()) / 3_600_000

  const checkOut = detail.materials.find((m) => m.phase === 'check_out') ?? null
  const checkIn = detail.materials.find((m) => m.phase === 'check_in') ?? null
  const reportOpen = editingReport || data.report === null

  function answer(response: AvailabilityResponse) {
    // The note explains the answer, so it travels with a change of mind rather
    // than being silently dropped.
    const keptNote = myAvailability?.note ?? null
    void action.run(
      () => respondToAvailability(eventId, coachId, response, keptNote),
      ANSWERS.find((a) => a.value === response)?.saved ?? 'Answer saved.',
    )
  }

  async function saveNote() {
    if (!myAvailability) return
    const ok = await action.run(
      () =>
        respondToAvailability(
          eventId,
          coachId,
          myAvailability.response,
          note.trim() ? note.trim() : null,
        ),
      'Note saved. Michel sees it next to your answer.',
    )
    if (ok) setNoteOpen(false)
  }

  function confirm() {
    if (!myAssignment) return
    void action.run(
      () => confirmAssignment(myAssignment.id),
      'Confirmed. Michel can see you are coming.',
    )
  }

  async function standDown() {
    if (!myAssignment) return
    const ok = await action.run(
      () => withdrawFromAssignment(myAssignment.id, reason.trim()),
      'Michel has been told. Message him too if it is soon.',
    )
    if (ok) {
      setReason('')
      setStandingDown(false)
    }
  }

  return (
    <>
      <Link to="/events" className="backlink">
        <ChevronLeftIcon />
        My events
      </Link>

      <PageHeader
        title={event.title}
        subtitle={`${dayLabel(event.starts_at, now)} at ${formatTime(event.starts_at)}`}
      />

      <div className="row row--wrap" style={{ marginBottom: 'var(--sp-5)' }}>
        <ServiceTag service={event.service} />
        <EventStatusBadge status={event.status} />
        {myAssignment ? <AssignmentBadge status={myAssignment.status} /> : null}
        <span className="spacer" />
        <span className="faint nowrap">
          {isCancelled ? '' : countdownLabel(event.starts_at, event.ends_at, now)}
        </span>
      </div>

      {error ? (
        <div style={{ marginBottom: 'var(--sp-4)' }}>
          <Notice tone="warning" title="Showing what was loaded earlier">
            {error}
          </Notice>
        </div>
      ) : null}

      {isCancelled ? (
        <div style={{ marginBottom: 'var(--sp-5)' }}>
          <Notice tone="warning" title="This event was cancelled">
            {event.cancellation_reason ?? 'No reason was recorded.'} You do not need to do
            anything, and nothing is held against you.
          </Notice>
        </div>
      ) : null}

      {myStandDown ? (
        <div style={{ marginBottom: 'var(--sp-5)' }}>
          <Notice tone="info" title="You came off this event">
            {myStandDown.cancelled_at ? `${relativeTime(myStandDown.cancelled_at)}. ` : ''}
            Reason on file: &ldquo;{myStandDown.cancellation_reason ?? 'none recorded'}&rdquo;{' '}
            {myStandDown.cancelled_at &&
            new Date(event.starts_at).getTime() - new Date(myStandDown.cancelled_at).getTime() <
              12 * 3_600_000
              ? 'This was inside 12 hours of the start, which clause 4.3 of your contract covers. Michel decides case by case, and any warning shows this reason next to it.'
              : 'Michel had time to find a replacement, so nothing goes on your record for this.'}
          </Notice>
        </div>
      ) : null}

      {/* ------------------------------------------------ the one live action */}

      {!isCancelled && canAnswer ? (
        <Section title={answered ? 'Your answer' : 'Can you work this one?'}>
          <Card tone={answered ? 'default' : 'attention'}>
            <p className="small muted">
              {myAvailability === null
                ? 'Nobody has asked you about this one yet. You are qualified for it, so you can tell Michel you are free.'
                : 'Michel needs an answer before he can staff it. Saying you are free is not a commitment, he still decides who goes.'}
            </p>

            <div
              className="btn-group btn-group--fill"
              role="group"
              aria-label="Your availability"
              style={{ marginTop: 'var(--sp-3)' }}
            >
              {ANSWERS.map((choice) => {
                const active = myAvailability?.response === choice.value
                return (
                  <button
                    key={choice.value}
                    type="button"
                    className={`btn${active ? '' : ' btn--secondary'}`}
                    aria-pressed={active}
                    disabled={action.busy}
                    onClick={() => answer(choice.value)}
                  >
                    {choice.label}
                  </button>
                )
              })}
            </div>

            {answered && myAvailability ? (
              <>
                <p className="faint" style={{ marginTop: 'var(--sp-3)' }}>
                  Answered {relativeTime(myAvailability.responded_at ?? myAvailability.updated_at)}.
                  You can change it until Michel assigns the event.
                </p>

                {noteOpen ? (
                  <div className="field" style={{ marginTop: 'var(--sp-3)', marginBottom: 0 }}>
                    <label htmlFor="availability-note">Anything Michel should know?</label>
                    <textarea
                      id="availability-note"
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="I can do it if it finishes by 17:00."
                    />
                    <div className="btn-group btn-group--fill">
                      <button
                        type="button"
                        className="btn btn--secondary"
                        disabled={action.busy}
                        onClick={() => setNoteOpen(false)}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn"
                        disabled={action.busy}
                        onClick={() => void saveNote()}
                      >
                        Save note
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={{ marginTop: 'var(--sp-2)' }}>
                    {myAvailability.note ? (
                      <p className="small muted">Your note: {myAvailability.note}</p>
                    ) : null}
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      style={{ paddingLeft: 0 }}
                      onClick={() => {
                        setNote(myAvailability.note ?? '')
                        setNoteOpen(true)
                      }}
                    >
                      {myAvailability.note ? 'Edit the note' : 'Add a note for Michel'}
                    </button>
                  </div>
                )}
              </>
            ) : null}

            <ActionFeedback error={action.error} done={action.done} />
          </Card>
        </Section>
      ) : null}

      {!isCancelled && myAssignment?.status === 'assigned' && phase !== 'finished' ? (
        <Section title="You are on this event">
          <Card tone="attention">
            <p className="small muted">
              Confirm so Michel knows you have seen it and he can stop looking. If something
              changes later, tell him as early as you can.
            </p>
            <button
              type="button"
              className="btn btn--block"
              style={{ marginTop: 'var(--sp-3)' }}
              disabled={action.busy}
              onClick={confirm}
            >
              {action.busy ? 'Saving' : 'Confirm I will be there'}
            </button>
            <ActionFeedback error={action.error} done={action.done} />
          </Card>
        </Section>
      ) : null}

      {!isCancelled && myAssignment?.status === 'confirmed' && phase === 'upcoming' ? (
        <div style={{ marginBottom: 'var(--sp-5)' }}>
          <Notice tone="success" title="You are confirmed">
            {countdownLabel(event.starts_at, event.ends_at, now)}. Reminders land 48 and 24 hours
            before.
          </Notice>
        </div>
      ) : null}

      {!isCancelled && phase === 'running' && myAssignment ? (
        <div style={{ marginBottom: 'var(--sp-5)' }}>
          <Notice tone="info" title="Happening now">
            The brief is below. Materials check-in and the report open when it ends at{' '}
            {formatTime(event.ends_at)}.
          </Notice>
        </div>
      ) : null}

      {/* ------------------------------------------------------------- report */}

      {!isCancelled && myAssignment && phase === 'finished' ? (
        <Section
          title="How did it go?"
          action={
            data.report && !editingReport ? (
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => setEditingReport(true)}
              >
                Edit
              </button>
            ) : null
          }
        >
          {reportOpen || !data.report ? (
            <EventReportForm
              eventId={eventId}
              coachId={coachId}
              existing={data.report}
              onSaved={afterReport}
            />
          ) : (
            <ReportSummary report={data.report} />
          )}
        </Section>
      ) : null}

      {/* -------------------------------------------------------------- brief */}

      <Section title="The brief">
        <Card>
          <dl className="deflist">
            <div>
              <dt>Date</dt>
              <dd>{formatDayLong(event.starts_at)}</dd>
            </div>
            <div>
              <dt>Time</dt>
              <dd>
                {formatTime(event.starts_at)} to {formatTime(event.ends_at)}
              </dd>
            </div>
            <div>
              <dt>Length</dt>
              <dd>{formatDuration(event.duration_minutes)}</dd>
            </div>
            <div>
              <dt>Service</dt>
              <dd>{event.service.name}</dd>
            </div>
            {event.location_name ? (
              <div>
                <dt>Venue</dt>
                <dd>{event.location_name}</dd>
              </div>
            ) : null}
            {event.location_address ? (
              <div>
                <dt>Address</dt>
                <dd>{event.location_address}</dd>
              </div>
            ) : null}
            <div>
              <dt>Area</dt>
              <dd>{humanize(event.location_zone)}</dd>
            </div>
            {event.client_name ? (
              <div>
                <dt>Client</dt>
                <dd>{event.client_name}</dd>
              </div>
            ) : null}
            <div>
              <dt>Team</dt>
              <dd>
                {staffing.assigned} of {staffing.required}{' '}
                {pluralize(staffing.required, 'coach', 'coaches')}
              </dd>
            </div>
            {myAssignment ? (
              <div>
                <dt>Your role</dt>
                <dd>{myAssignment.is_lead ? 'Lead coach' : 'Coach'}</dd>
              </div>
            ) : null}
            {myAssignment && event.client_contact ? (
              <div>
                <dt>Client contact</dt>
                <dd>
                  <a href={`tel:${event.client_contact}`}>{event.client_contact}</a>
                </dd>
              </div>
            ) : null}
          </dl>

          {event.location_address ? (
            <a
              className="btn btn--secondary btn--block"
              style={{ marginTop: 'var(--sp-4)' }}
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                `${event.location_name ?? ''} ${event.location_address}`,
              )}`}
              target="_blank"
              rel="noreferrer"
            >
              Open in Maps
            </a>
          ) : null}

          <p className="faint" style={{ marginTop: 'var(--sp-3)' }}>
            All times are Dubai time.
          </p>
        </Card>
      </Section>

      {event.brief ? (
        <Section title="Notes from Michel">
          <div className="brief">{event.brief}</div>
        </Section>
      ) : null}

      {/* ---------------------------------------------------------- materials */}

      {checkOut || checkIn ? (
        <Section title="Materials">
          <div className="stack">
            {checkOut ? (
              <EventMaterialsCard
                check={checkOut}
                coachId={coachId}
                canSign={Boolean(myAssignment) && !isCancelled && phase !== 'finished'}
                lockedReason={
                  myAssignment
                    ? 'The event is over, so this list is history now.'
                    : 'Only the coaches assigned to this event sign the crate out.'
                }
                onChanged={reload}
              />
            ) : null}
            {checkIn ? (
              <EventMaterialsCard
                check={checkIn}
                coachId={coachId}
                canSign={Boolean(myAssignment) && !isCancelled && phase === 'finished'}
                lockedReason={
                  myAssignment
                    ? `Check-in opens when the event ends at ${formatTime(event.ends_at)}.`
                    : 'Only the coaches assigned to this event sign the crate back in.'
                }
                onChanged={reload}
              />
            ) : null}
          </div>
        </Section>
      ) : null}

      {/* --------------------------------------------------------------- team */}

      <Section title="Who else is on it" count={teammates.length}>
        {teammates.length === 0 ? (
          <EmptyState
            title="Nobody else yet"
            body={
              staffing.remaining > 0
                ? `Michel is still looking for ${staffing.remaining} more.`
                : 'You are covering this one on your own.'
            }
          />
        ) : (
          <div className="stack stack--tight">
            {teammates.map((row) => (
              <div key={row.id} className="rowitem">
                <Avatar initials={initials(row.coach.full_name)} size={36} />
                <div className="rowitem__main">
                  <p className="rowitem__title">{row.coach.full_name}</p>
                  <p className="rowitem__sub">
                    {row.is_lead ? 'Lead coach' : humanize(row.coach.seniority)}
                  </p>
                </div>
                <AssignmentBadge status={row.status} />
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* --------------------------------------------------------- stand down */}

      {myAssignment && !isCancelled && phase === 'upcoming' ? (
        <Section title="Something changed?">
          {standingDown ? (
            <Card>
              <div style={{ marginBottom: 'var(--sp-3)' }}>
                {hoursToStart < 12 ? (
                  <Notice tone="warning" title="This is inside 12 hours">
                    Standing down this late is covered by clause 4.3 of your contract, and Michel
                    has very little time to replace you. Any warning is issued by hand, always with
                    your reason next to it, and can be revoked if the reason justifies it. Your
                    answer for this event changes to &ldquo;Cannot make it&rdquo;. Call him as well
                    as sending this.
                  </Notice>
                ) : (
                  <Notice tone="info" title="What happens next">
                    Your reason goes to Michel with the time you sent it. He has{' '}
                    {Math.floor(hoursToStart / 24) >= 1
                      ? `${Math.floor(hoursToStart / 24)} ${pluralize(Math.floor(hoursToStart / 24), 'day')}`
                      : `${Math.floor(hoursToStart)} hours`}{' '}
                    to find a replacement, so nothing goes on your record. Clause 4.3 of your
                    contract only applies inside 12 hours of the start. Your answer for this event
                    changes to &ldquo;Cannot make it&rdquo; so his board stays right.
                  </Notice>
                )}
              </div>
              <div className="field">
                <label htmlFor="stand-down-reason">Why can you not make it?</label>
                <textarea
                  id="stand-down-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Family emergency, I am sorry. Free again from Sunday."
                />
                <p className="field__help">Michel sees this. He needs it to find a replacement.</p>
              </div>
              <div className="btn-group btn-group--fill">
                <button
                  type="button"
                  className="btn btn--secondary"
                  disabled={action.busy}
                  onClick={() => setStandingDown(false)}
                >
                  Keep me on it
                </button>
                <button
                  type="button"
                  className="btn btn--accent"
                  disabled={action.busy || reason.trim().length === 0}
                  onClick={() => void standDown()}
                >
                  Take me off
                </button>
              </div>
              <ActionFeedback error={action.error} done={action.done} />
            </Card>
          ) : (
            <button
              type="button"
              className="btn btn--danger btn--block"
              onClick={() => setStandingDown(true)}
            >
              I can no longer make it
            </button>
          )}
        </Section>
      ) : null}

      {myAvailability && !canAnswer && !myAssignment ? (
        <div className="row row--wrap" style={{ marginBottom: 'var(--sp-5)' }}>
          <span className="small muted">Your answer was:</span>
          <AvailabilityBadge response={myAvailability.response} />
        </div>
      ) : null}
    </>
  )
}

/** The report as filed, once it is in. Editing reopens the form above it. */
function ReportSummary({ report }: { report: PostEventReport }) {
  return (
    <Card tone="muted">
      <div className="row row--between">
        <p className="card__title">Report filed</p>
        <Badge tone="positive">Done</Badge>
      </div>
      <dl className="deflist" style={{ marginTop: 'var(--sp-3)' }}>
        <div>
          <dt>Sent</dt>
          <dd>{formatDateTime(report.submitted_at)}</dd>
        </div>
        {report.overall_rating !== null ? (
          <div>
            <dt>Rating</dt>
            <dd>{report.overall_rating} of 5</dd>
          </div>
        ) : null}
        {report.attendance_count !== null ? (
          <div>
            <dt>Children</dt>
            <dd>{report.attendance_count}</dd>
          </div>
        ) : null}
        {report.incident ? (
          <div>
            <dt>Incident</dt>
            <dd>Recorded, Michel has it</dd>
          </div>
        ) : null}
      </dl>
      {report.went_well ? (
        <p className="small muted" style={{ marginTop: 'var(--sp-3)' }}>
          {report.went_well}
        </p>
      ) : null}
    </Card>
  )
}
